// A token endpoint error page is untrusted input. Only fixed labels and values
// with narrow formats may leave this parser; the HTML itself is never retained.
export interface Cloudflare502Diagnostic {
  source: 'Cloudflare error page';
  status: '502 Bad Gateway';
  host?: string;
  timestamp?: string;
  rayId?: string;
  browserStatus?: 'Working' | 'Error';
  cloudflareStatus?: 'Working' | 'Error';
  hostStatus?: 'Working' | 'Error';
}

const MAX_HTML_LENGTH = 64 * 1024;
const RAY_ID = /^[a-f0-9]{16}(?:-[a-z]{3})?$/i;

export function cloudflare502Diagnostic(
  body: string,
  expectedHost: string | undefined,
  headerRayId?: string | null
): Cloudflare502Diagnostic | undefined {
  if (body.length > MAX_HTML_LENGTH ||
      !/\bid=["']cf-error-details["']/i.test(body) ||
      !/Cloudflare/i.test(body) ||
      !/\b502\s*:?\s*Bad gateway\b/i.test(body)) return undefined;

  // Use the text only for matching known diagnostic fields. It is never shown.
  const text = body
    .replace(/<(?:script|style)\b[^>]*>[\s\S]*?<\/(?:script|style)\s*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ');
  const rayFromBody = /Cloudflare Ray ID:\s*([a-f0-9]{16}(?:-[a-z]{3})?)(?![\w-])/i.exec(text)?.[1];
  const rayId = [rayFromBody, headerRayId?.trim()].find(value => value && RAY_ID.test(value));
  const timestamp = /\b\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} UTC\b/.exec(text)?.[0];
  const validTimestamp = timestamp && !Number.isNaN(Date.parse(timestamp.replace(' ', 'T').replace(' UTC', 'Z')))
    ? timestamp : undefined;
  // The host is taken from the current request origin, never copied from HTML.
  const host = expectedHost && /^[a-z0-9.-]{1,253}$/i.test(expectedHost) &&
    text.match(/[a-z0-9.-]+/gi)?.some(part => part.toLowerCase() === expectedHost.toLowerCase())
    ? expectedHost : undefined;
  const sourceStatus = (label: string): 'Working' | 'Error' | undefined => {
    const match = new RegExp(`\\b${label}\\s+(Working|Error)\\b`, 'i').exec(text);
    return match ? (match[1].toLowerCase() === 'working' ? 'Working' : 'Error') : undefined;
  };

  return {
    source: 'Cloudflare error page',
    status: '502 Bad Gateway',
    host,
    timestamp: validTimestamp,
    rayId,
    browserStatus: sourceStatus('Browser'),
    cloudflareStatus: sourceStatus('Cloudflare'),
    hostStatus: sourceStatus('Host')
  };
}
